import React, { useEffect, useMemo, useState } from "react";

import { practiceSchedulesService } from "api/services/practice-schedules.service";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import Modal from "components/UI/Modal";
import { getApiErrorMessage } from "utils/api-error";

import type { PracticeSchedule, PracticeType } from "api/types/practice-schedules.types";

import "./ScheduleDetailModal.scss";
import "./PracticeScheduleDetailModal.scss";

interface PracticeScheduleDetailModalProps {
  open: boolean;
  scheduleId: number | null;
  onClose: () => void;
  onSaved?: () => void;
}

const STATUS_BADGE: Record<string, "active" | "rejected" | "draft"> = {
  OPEN: "active",
  CLOSED: "draft",
  CANCELLED: "rejected",
};

const PRICING_BADGE: Record<string, "active" | "info"> = {
  FREE: "info",
  PAID: "active",
};

const PRACTICE_TYPE_OPTIONS: { value: PracticeType; label: string }[] = [
  { value: "SELF_PRACTICE", label: "자율 연습" },
  { value: "REGULAR_TRAINING", label: "정기 훈련" },
];

type EditableKey = "title" | "date" | "start_time" | "end_time" | "capacity" | "practice_type" | "allow_ticket" | "price" | "discount_price";

interface FormState {
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  capacity: number;
  practice_type: PracticeType;
  allow_ticket: boolean;
  price: string;
  discount_price: string;
}

const formatDateTime = (value: string | null) => {
  if (!value) return "-";
  return value.replace("T", " ").slice(0, 16);
};

const toFormState = (d: PracticeSchedule): FormState => ({
  title: d.title ?? "",
  date: d.date,
  start_time: d.start_time.slice(0, 5),
  end_time: d.end_time.slice(0, 5),
  capacity: d.capacity,
  practice_type: d.practice_type,
  allow_ticket: d.allow_ticket,
  price: d.price ?? "",
  discount_price: d.discount_price ?? "",
});

const getEditableKeys = (detail: PracticeSchedule): Set<EditableKey> => {
  // 예약자가 있으면 정원만 수정 가능
  if (detail.current_bookings > 0) {
    return new Set<EditableKey>(["capacity"]);
  }
  const keys: EditableKey[] = ["title", "date", "start_time", "end_time", "capacity", "practice_type", "allow_ticket"];
  // 이미 유료인 경우에만 가격 직접 수정 가능 (FREE → PAID 는 유료 전환 버튼 사용)
  if (detail.pricing_type === "PAID") {
    keys.push("price", "discount_price");
  }
  return new Set<EditableKey>(keys);
};

interface FieldProps {
  label: string;
  full?: boolean;
  multiline?: boolean;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, full, multiline, children }) => (
  <div className={`detail-field${full ? " detail-field--full" : ""}`}>
    <label className="detail-field__label">{label}</label>
    <div className={`detail-field__value${multiline ? " detail-field__value--multiline" : ""}`}>{children}</div>
  </div>
);

const PracticeScheduleDetailModal: React.FC<PracticeScheduleDetailModalProps> = ({ open, scheduleId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<PracticeSchedule | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  // 유료 전환 인라인 폼
  const [showConvertForm, setShowConvertForm] = useState(false);
  const [convertPrice, setConvertPrice] = useState("");
  const [convertDiscountPrice, setConvertDiscountPrice] = useState("");
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState("");

  const fetchDetail = async () => {
    if (!scheduleId) return;
    setLoading(true);
    setError("");
    setEditMode(false);
    setShowConvertForm(false);
    setConvertError("");
    try {
      const res = await practiceSchedulesService.get(scheduleId);
      setDetail(res.data);
      setForm(toFormState(res.data));
    } catch (err) {
      setDetail(null);
      setForm(null);
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !scheduleId) return;
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheduleId]);

  const editableKeys = useMemo(() => (detail ? getEditableKeys(detail) : new Set<EditableKey>()), [detail]);
  const canEdit = (key: EditableKey) => editMode && editableKeys.has(key);

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const buildPayload = (): Record<string, unknown> => {
    if (!detail || !form) return {};
    const payload: Record<string, unknown> = {};
    const original = toFormState(detail);

    editableKeys.forEach((key) => {
      const next = form[key];
      const prev = original[key];
      if (next !== prev) {
        if (key === "start_time" || key === "end_time") {
          payload[key] = `${form[key]}:00`;
        } else if (key === "price" || key === "discount_price") {
          payload[key] = form[key] === "" ? null : Number(form[key]);
        } else {
          payload[key] = next;
        }
      }
    });
    return payload;
  };

  const handleSave = async () => {
    if (!scheduleId || !form || !detail) return;
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) {
      setEditMode(false);
      return;
    }
    setSaving(true);
    try {
      const res = await practiceSchedulesService.update(scheduleId, payload);
      setDetail(res.data);
      setForm(toFormState(res.data));
      setEditMode(false);
      onSaved?.();
    } catch (err) {
      alert(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (detail) setForm(toFormState(detail));
    setEditMode(false);
  };

  const handleConvertSubmit = async () => {
    if (!scheduleId || !convertPrice) {
      setConvertError("가격을 입력하세요.");
      return;
    }
    setConverting(true);
    setConvertError("");
    try {
      await practiceSchedulesService.convertToPaid(scheduleId, {
        price: Number(convertPrice),
        discount_price: convertDiscountPrice ? Number(convertDiscountPrice) : null,
      });
      setShowConvertForm(false);
      setConvertPrice("");
      setConvertDiscountPrice("");
      await fetchDetail();
      onSaved?.();
    } catch (err) {
      setConvertError(getApiErrorMessage(err));
    } finally {
      setConverting(false);
    }
  };

  const renderEditNotice = () => {
    if (!editMode || !detail) return null;
    if (detail.current_bookings > 0) {
      return <div className="detail-notice detail-notice--warn">예약자가 있는 연습반은 정원(capacity)만 수정 가능합니다.</div>;
    }
    return <div className="detail-notice detail-notice--info">예약자가 없는 연습반은 기본 정보·일정·요금 항목을 수정할 수 있습니다.</div>;
  };

  const canConvert = !!detail && detail.pricing_type === "FREE" && detail.status === "OPEN";

  const renderConvertSection = () => {
    if (!detail) return null;

    return (
      <section className="detail-section">
        <h4 className="detail-section__title">관리자 작업</h4>

        <div className="detail-notice detail-notice--info">유료 전환은 기본적으로 강사가 앱에서 직접 신청합니다. 신청이 어려운 경우에만 관리자가 강제 전환하세요.</div>

        {detail.pricing_type === "PAID" ? (
          <div className="practice-detail__convert-info">이미 유료 연습반입니다. 가격은 수정 모드에서 변경할 수 있습니다.</div>
        ) : !canConvert ? (
          <div className="practice-detail__convert-info">상태가 OPEN인 무료 연습반에서만 유료 전환이 가능합니다. (현재: {detail.status})</div>
        ) : !showConvertForm ? (
          <div className="practice-detail__convert-actions">
            <Button variant="primary" size="sm" onClick={() => setShowConvertForm(true)}>
              유료 전환 (강제)
            </Button>
          </div>
        ) : (
          <div className="practice-detail__convert-form">
            <div className="practice-detail__convert-row">
              <label className="detail-field__label">가격 (원)</label>
              <input className="detail-input" type="number" min={0} placeholder="예: 30000" value={convertPrice} onChange={(e) => setConvertPrice(e.target.value)} />
            </div>
            <div className="practice-detail__convert-row">
              <label className="detail-field__label">할인 가격 (선택)</label>
              <input className="detail-input" type="number" min={0} placeholder="예: 25000" value={convertDiscountPrice} onChange={(e) => setConvertDiscountPrice(e.target.value)} />
            </div>
            {convertError && <div className="reservation-modal__error">{convertError}</div>}
            <div className="practice-detail__convert-actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowConvertForm(false);
                  setConvertPrice("");
                  setConvertDiscountPrice("");
                  setConvertError("");
                }}
                disabled={converting}
              >
                취소
              </Button>
              <Button variant="primary" size="sm" onClick={handleConvertSubmit} disabled={converting || !convertPrice}>
                {converting ? "전환 중..." : "전환하기"}
              </Button>
            </div>
          </div>
        )}
      </section>
    );
  };

  const renderContent = () => {
    if (loading) return <div className="admin-page__loading">불러오는 중...</div>;
    if (error) return <div className="admin-page__error">{error}</div>;
    if (!detail || !form) return null;

    const priceView = detail.current_price ? `${Number(detail.current_price).toLocaleString()}원` : "-";

    return (
      <form className="schedule-detail" onSubmit={(e) => e.preventDefault()}>
        {renderEditNotice()}

        <section className="detail-section">
          <h4 className="detail-section__title">기본 정보</h4>
          <div className="detail-section__grid">
            {canEdit("title") ? (
              <Field label="제목" full>
                <input className="detail-input" type="text" value={form.title} onChange={(e) => updateField("title", e.target.value)} placeholder="연습반 제목" />
              </Field>
            ) : (
              <Field label="제목" full>
                {detail.title || "-"}
              </Field>
            )}

            <Field label="스포츠">{detail.sport_name || "-"}</Field>
            <Field label="강사">{detail.instructor_name}</Field>

            {canEdit("practice_type") ? (
              <Field label="유형">
                <select className="detail-input" value={form.practice_type} onChange={(e) => updateField("practice_type", e.target.value as PracticeType)}>
                  {PRACTICE_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <Field label="유형">{detail.practice_type === "SELF_PRACTICE" ? "자율 연습" : "정기 훈련"}</Field>
            )}

            {canEdit("allow_ticket") ? (
              <Field label="티켓 사용 허용">
                <label className="detail-check">
                  <input type="checkbox" checked={form.allow_ticket} onChange={(e) => updateField("allow_ticket", e.target.checked)} />
                  티켓 사용 허용
                </label>
              </Field>
            ) : (
              <Field label="티켓 사용 허용">{detail.allow_ticket ? "허용" : "불가"}</Field>
            )}
          </div>
        </section>

        <section className="detail-section">
          <h4 className="detail-section__title">일정 · 장소</h4>
          <div className="detail-section__grid">
            <Field label="날짜">{canEdit("date") ? <input className="detail-input" type="date" value={form.date} onChange={(e) => updateField("date", e.target.value)} /> : detail.date}</Field>
            <Field label="시간">
              {canEdit("start_time") || canEdit("end_time") ? (
                <div className="detail-time-range">
                  <input className="detail-input" type="time" value={form.start_time} onChange={(e) => updateField("start_time", e.target.value)} disabled={!canEdit("start_time")} />
                  <span>~</span>
                  <input className="detail-input" type="time" value={form.end_time} onChange={(e) => updateField("end_time", e.target.value)} disabled={!canEdit("end_time")} />
                </div>
              ) : (
                `${detail.start_time.slice(0, 5)} ~ ${detail.end_time.slice(0, 5)}`
              )}
            </Field>
            <Field label="장소" full>
              {detail.location_name}
            </Field>
          </div>
        </section>

        <section className="detail-section">
          <h4 className="detail-section__title">예약 현황 · 상태</h4>
          <div className="detail-section__grid">
            <Field label="정원">{canEdit("capacity") ? <input className="detail-input" type="number" min={detail.current_bookings} value={form.capacity} onChange={(e) => updateField("capacity", Number(e.target.value))} /> : `${detail.capacity}명`}</Field>
            <Field label="예약 인원">{detail.current_bookings}명</Field>
            <Field label="대기자">{detail.waiting_count}명</Field>
            <Field label="스케줄 상태">
              <Badge variant={STATUS_BADGE[detail.status] ?? "draft"}>{detail.status}</Badge>
            </Field>
          </div>
        </section>

        <section className="detail-section">
          <h4 className="detail-section__title">요금</h4>
          <div className="detail-section__grid">
            <Field label="유형">
              <Badge variant={PRICING_BADGE[detail.pricing_type] ?? "draft"}>{detail.pricing_type === "FREE" ? "무료" : "유료"}</Badge>
            </Field>
            <Field label="현재 가격">{priceView}</Field>

            {canEdit("price") ? (
              <Field label="가격 (원)">
                <input className="detail-input" type="number" min={0} value={form.price} onChange={(e) => updateField("price", e.target.value)} placeholder="예: 30000" />
              </Field>
            ) : null}

            {canEdit("discount_price") ? (
              <Field label="할인 가격 (원)">
                <input className="detail-input" type="number" min={0} value={form.discount_price} onChange={(e) => updateField("discount_price", e.target.value)} placeholder="비우면 미적용" />
              </Field>
            ) : null}

            {detail.pricing_type === "PAID" ? (
              <>
                <Field label="결제 대기">{detail.pending_payment_count}건</Field>
                <Field label="결제 완료">{detail.paid_count}건</Field>
              </>
            ) : null}
          </div>
        </section>

        {renderConvertSection()}

        <section className="detail-section">
          <h4 className="detail-section__title">기록</h4>
          <div className="detail-section__grid">
            <Field label="생성일">{formatDateTime(detail.created_at)}</Field>
            <Field label="수정일">{formatDateTime(detail.updated_at)}</Field>
          </div>
        </section>
      </form>
    );
  };

  const renderFooter = () => {
    if (!detail) return null;
    if (editMode) {
      return (
        <div className="modal-footer-actions">
          <Button variant="secondary" onClick={handleCancelEdit} disabled={saving}>
            취소
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || editableKeys.size === 0}>
            {saving ? "저장 중..." : "저장"}
          </Button>
        </div>
      );
    }
    return (
      <div className="modal-footer-actions">
        <Button variant="primary" onClick={() => setEditMode(true)}>
          수정하기
        </Button>
      </div>
    );
  };

  return (
    <Modal open={open} title="연습반 상세" width="760px" onClose={onClose} footer={renderFooter()} closeOnOverlayClick={false}>
      {renderContent()}
    </Modal>
  );
};

export default PracticeScheduleDetailModal;
